import { ThemeProvider } from "@emotion/react";
import {
  ExpandMore,
  Save,
  Cancel,
  InfoOutlined,
  ArrowForward,
  FileDownload,
  PictureAsPdf,
} from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { RuleGroup } from "react-querybuilder";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import {
  Autocomplete,
  AutocompleteValue,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  PaletteMode,
  TextField,
  ThemeOptions,
  createTheme,
  CircularProgress,
  Tooltip,
  Fade,
  Alert,
  Paper,
  Typography,
  alpha,
  Divider,
  Skeleton,
  Chip,
  useMediaQuery,
  useTheme,
  Snackbar,
  DialogActions,
  DialogContentText,
} from "@mui/material";

import MDButton from "components/MDButton";
import DashboardLayout from "lib/LayoutContainers/DashboardLayout";
import withDommer from "pages/withDommer";

import { QueryBuilderMaterial } from "@react-querybuilder/material";

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { ActionProps, Field, QueryBuilder, RuleGroupType, formatQuery } from "react-querybuilder";
import { Collapse } from "@mui/material";

import { post, get, put } from "requests";
import DataTable from "lib/Tables/DataTable";
import { del } from "requests";

const dealsFieldUrl = "/reports/headers";
const dealsExecQueryUrl = "/reports/execQuery ";
const listPublicViews = "/reports/listPublicViews";
const reportsBaseUrl = "/reports";

const getTheme = (mode: PaletteMode) =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: "#26c6da",
        light: "#80deea",
        dark: "#00acc1",
      },
      secondary: {
        main: "#2F929F",
        light: "#2F929F",
        dark: "#2F929F",
      },
      error: {
        main: "#f44336",
      },
      warning: {
        main: "#ff9800",
      },
      info: {
        main: "#03a9f4",
      },
      success: {
        main: "#4caf50",
      },
      background: {
        default: mode === "dark" ? "#121212" : "#f8f9fa",
        paper: mode === "dark" ? "#1e1e1e" : "#ffffff",
      },
    },
    typography: {
      fontFamily:
        '"-apple-system", "BlinkMacSystemFont", "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
      button: {
        textTransform: "none",
        fontWeight: 500,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: "0 8px",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.6em",
            boxShadow:
              mode === "dark" ? "0 4px 6px rgba(0, 0, 0, 0.3)" : "0 2px 4px rgba(0, 0, 0, 0.1)",
            transition: "all 0.25s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow:
                mode === "dark" ? "0 6px 10px rgba(0, 0, 0, 0.4)" : "0 4px 8px rgba(0, 0, 0, 0.15)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
            "&:focus-visible": {
              outline: `2px solid ${mode === "dark" ? "#80deea" : "#26c6da"}`,
              outlineOffset: "2px",
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            fontSize: "0.875rem",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: "bold",
            backgroundColor: mode === "dark" ? "#311b92" : "#ede7f6",
            color: mode === "dark" ? "#fff" : "#311b92",
          },
          root: {
            borderBottom: mode === "dark" ? "1px solid #333" : "1px solid #ddd",
            padding: "12px 16px",
            fontSize: "0.85rem",
            transition: "background-color 0.15s ease",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor:
                mode === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.02)",
            },
            transition: "background-color 0.15s ease",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            borderRadius: 12,
            transition: "box-shadow 0.3s ease",
          },
          elevation1: {
            boxShadow:
              mode === "dark" ? "0 3px 10px rgba(0, 0, 0, 0.35)" : "0 2px 8px rgba(0, 0, 0, 0.08)",
          },
        },
      },
      MuiCollapse: {
        styleOverrides: {
          root: {
            transition: "height 300ms cubic-bezier(0.4, 0, 0.2, 1) !important",
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            transition: "all 0.2s ease",
          },
          inputRoot: {
            padding: "6px 12px !important",
          },
          tag: {
            margin: "2px",
          },
          listbox: {
            padding: "8px",
            "& .MuiAutocomplete-option": {
              padding: "8px 12px",
              borderRadius: "6px",
              margin: "2px 0",
              transition: "all 0.1s ease",
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            transition: "all 0.2s ease",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            transition: "all 0.2s ease",
            "&:hover": {
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            },
          },
          colorPrimary: {
            backgroundColor: alpha("#26c6da", 0.15),
            color: "#00acc1",
            "&:hover": {
              backgroundColor: alpha("#26c6da", 0.25),
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: "0.75rem",
            padding: "8px 12px",
            backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.75)",
            color: mode === "dark" ? "#000" : "#fff",
            boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
            borderRadius: 6,
            maxWidth: 300,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontSize: "0.85rem",
            alignItems: "center",
          },
          icon: {
            opacity: 0.85,
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          },
        },
      },
    },
  } as unknown as ThemeOptions);

const RemoveRuleButton = ({ handleOnClick }: ActionProps) => (
  <DeleteIcon
    color="error"
    fontSize="small"
    onClick={handleOnClick}
    style={{
      cursor: "pointer",
      transition: "transform 0.2s ease, opacity 0.2s ease",
      opacity: 0.75,
    }}
    className="delete-icon"
    sx={{
      "&:hover": {
        transform: "scale(1.15)",
        opacity: 1,
      },
    }}
    aria-label="Remove rule"
    role="button"
    tabIndex={0}
  />
);

const RemoveGroupButton = ({ handleOnClick }: ActionProps) => (
  <DeleteIcon
    color="error"
    fontSize="small"
    onClick={handleOnClick}
    style={{
      cursor: "pointer",
      transition: "transform 0.2s ease, opacity 0.2s ease",
      opacity: 0.75,
    }}
    className="delete-icon"
    sx={{
      "&:hover": {
        transform: "scale(1.15)",
        opacity: 1,
      },
    }}
    aria-label="Remove group"
    role="button"
    tabIndex={0}
  />
);

const CustomGroup = (props: any) => {
  const level = props.path.length;
  const bgColors = [
    "rgba(240, 248, 255, 0.7)",
    "rgba(230, 255, 230, 0.7)",
    "rgba(255, 245, 230, 0.7)",
    "rgba(255, 230, 242, 0.7)",
    "rgba(230, 242, 255, 0.7)",
  ];
  const bg = bgColors[level % bgColors.length];

  return (
    <div
      style={{
        backgroundColor: bg,
        padding: "0.75rem",
        borderRadius: 8,
        margin: "8px 0",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        transition: "all 0.3s ease",
        border: "1px solid rgba(0,0,0,0.05)",
      }}
      role="group"
      aria-label={`Rule group level ${level + 1}`}
    >
      <RuleGroup {...props} />
    </div>
  );
};

const customControls = {
  removeRuleAction: RemoveRuleButton,
  removeGroupAction: RemoveGroupButton,
  ruleGroup: CustomGroup,
};

const initialQuery: RuleGroupType = {
  combinator: "and",
  rules: [],
};

/**
 *  type: "column" => Just converts to title case
 *        "dataSource" => converts to title case and removes public and view words from the name
 */
function toTitleCase(str: string, type: "column" | "dataSource"): string {
  switch (type) {
    case "column":
      return str.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
    case "dataSource":
      return str
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase())
        .split(" ")
        .filter((_value, index, arr) => index != 0 && index != arr.length - 1)
        .join(" ");
    default:
      return str;
  }
}

function mapDataTypeToInputType(dataType: string): "text" | "number" | "date" {
  if (dataType.includes("timestamp") || dataType.includes("date")) return "date";
  if (dataType.includes("numeric") || dataType.includes("integer")) return "number";
  return "text";
}

function createFields(data: { column_name: string; data_type: string }[]): Field[] {
  return data.map((col) => ({
    name: col.column_name,
    label: toTitleCase(col.column_name, "column"),
    value: col.column_name,
    inputType: mapDataTypeToInputType(col.data_type),
  }));
}

type selectOption = {
  label: string;
  value: string;
};

type userReportsType = {
  id: number;
  reportName: string;
  report: {
    dataSource: string;
    selectedHeaders: selectOption[];
    sql: RuleGroupType;
  };
};

type SnackbarState = {
  open: boolean;
  message: string;
  severity: "success" | "error" | "info" | "warning";
};

export const Reports = () => {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState(initialQuery);
  const [allHeaders, setAllHeaders] = useState<Field[]>([]);
  const [selectedHeaders, setSelectedHeaders] = useState<selectOption[]>([]);
  const [dataSource, setDataSource] = useState<selectOption[]>([]);
  const [reportData, setReportData] = useState([]);
  const [userReports, setUserReports] = useState<userReportsType[]>([]);
  const [showReportNameDialog, setShowReportNameDialog] = useState<boolean>(false);
  const [newReportName, setNewReportName] = useState<string>("");
  const [selectedDataSource, setSelectedDataSource] = useState<selectOption | null>(null);
  const [selectedReport, setSelectedReport] = useState<{ label: string; value: number } | null>(
    null
  );

  const [updateMode, setUpdateMode] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingOperation, setLoadingOperation] = useState<string | null>(null);

  const [execQueryError, setExecQueryError] = useState<string[]>([]);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "info",
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);

  const theme = useMemo(() => getTheme("light"), []);
  const muiTheme = useTheme();
  const userId = useMemo(() => localStorage.getItem("logInUserId"), []);
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const fieldSelectRef = useRef<HTMLDivElement>(null);

  const showNotification = (message: string, severity: SnackbarState["severity"]) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const exportToCSV = useCallback(
    (providedFileName: string, dataSource: string) => {
      if (allHeaders === undefined || dataSource === "") {
        showNotification("No data available to export", "warning");
        return;
      }

      setLoadingOperation("exportCSV");
      try {
        let filename;
        if (providedFileName != "") {
          filename = `${providedFileName}-${dataSource}.csv`;
        } else {
          filename = `${dataSource}.csv`;
        }

        // Prepare only selected headers
        const header = selectedHeaders.map((v) => v.label).join(",");

        // Prepare data rows only for selected headers
        const data = reportData.map((row) =>
          selectedHeaders.map((fieldName) => JSON.stringify(row[fieldName.value] ?? "")).join(",")
        );

        const csvContent = [header, ...data].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showNotification(`Successfully exported ${data.length} rows to ${filename}`, "success");
      } catch (error) {
        showNotification("Failed to export data to CSV", "error");
        console.error("CSV export error:", error);
      } finally {
        setLoadingOperation(null);
      }
    },
    [allHeaders, reportData, selectedHeaders]
  );

  const exportToPDF = useCallback(
    (providedFileName: string, dataSource: string) => {
      if (reportData.length === 0 || !selectedDataSource) {
        showNotification("No data available to export", "warning");
        return;
      }

      setLoadingOperation("exportPDF");
      try {
        // Create a filename
        let filename: any;
        if (providedFileName !== "") {
          filename = `${providedFileName}-${dataSource}.pdf`;
        } else {
          filename = `${dataSource}.pdf`;
        }

        // Get the data table element
        const tableElement = document.querySelector(".MuiTable-root")?.parentElement;
        if (!tableElement) {
          throw new Error("Could not find table element");
        }

        // Use html2canvas to capture the table
        html2canvas(tableElement).then((canvas) => {
          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF({
            orientation: "landscape",
            unit: "mm",
          });

          // Add title
          pdf.setFontSize(16);
          pdf.text(dataSource, 15, 15);

          // Add report date
          const today = new Date();
          pdf.setFontSize(10);
          pdf.text(`Generated on: ${today.toLocaleDateString()}`, 15, 22);

          // Calculate dimensions to fit the page
          const imgProps = pdf.getImageProperties(imgData);
          const pdfWidth = pdf.internal.pageSize.getWidth() - 30; // margins
          const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

          // Add the table image
          pdf.addImage(imgData, "PNG", 15, 30, pdfWidth, pdfHeight);

          // Save the PDF
          pdf.save(filename);

          showNotification(`Successfully exported to PDF: ${filename}`, "success");
        });
      } catch (error) {
        showNotification("Failed to export data to PDF", "error");
        console.error("PDF export error:", error);
      } finally {
        setLoadingOperation(null);
      }
    },
    [reportData, selectedDataSource, selectedHeaders, showNotification]
  );
  const onQueryChangeFn = useCallback(
    (query: RuleGroupType) => {
      if (selectedReport) {
        setUpdateMode(true);
      }
      setExecQueryError([]);
      setQuery(query);
    },
    [selectedReport]
  );

  const generateReport = useCallback(() => {
    if (selectedHeaders.length === 0 || !selectedDataSource) {
      showNotification("Please select both data source and at least one data field", "warning");
      return;
    }

    setLoading(true);
    setLoadingOperation("runReport");

    const sqlQuery = formatQuery(query, {
      format: "sql",
      quoteFieldNamesWith: '"',
    });

    post(dealsExecQueryUrl, {
      query: sqlQuery,
      viewName: selectedDataSource.value,
    })
      .then((res) => {
        setReportData(res.data);
        const recordCount = res.data.length;
        showNotification(
          `Report generated successfully. ${recordCount} ${
            recordCount === 1 ? "record" : "records"
          } found.`,
          "success"
        );
      })
      .catch((res) => {
        if (res.status == 400) {
          setExecQueryError([res.response.data.message]);
        } else {
          setExecQueryError([res.response.data.message]);
        }
        showNotification("Failed to generate report", "error");
      })
      .finally(() => {
        setLoading(false);
        setLoadingOperation(null);
      });
  }, [query, selectedDataSource, selectedHeaders]);

  const getUserReports = useCallback(() => {
    setLoading(true);
    get(`${reportsBaseUrl}/${userId}`).then((res) => {
      setUserReports(res.data);
      if (res.data.length > 0) {
        onReportSelect(res.data[0]?.id, res.data);
      }
      setLoading(false);
    });
  }, [userId]);

  const getHeadersFromView = useCallback((viewName: String) => {
    setLoading(true);
    get(dealsFieldUrl + "/" + viewName)
      .then((res) => {
        setAllHeaders(createFields(res.data));
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const onReportSelect = useCallback(
    (selectReportId: number, passedUserReport: userReportsType[] | null) => {
      let report;
      if (passedUserReport !== null) {
        report = passedUserReport.find((r) => r.id === selectReportId);
      } else {
        report = userReports.find((r) => r.id === selectReportId);
      }
      if (!report) {
        setSelectedReport(null);
        return;
      }

      setSelectedReport({ label: report.reportName, value: report.id });
      setSelectedHeaders(report.report.selectedHeaders);
      const dataSourceValue = {
        label: toTitleCase(report.report.dataSource, "dataSource"),
        value: report.report.dataSource,
      };

      setQuery(report.report.sql);
      setUpdateMode(false);
      getHeadersFromView(dataSourceValue.value);
      setSelectedDataSource(dataSourceValue);
      showNotification(`Report "${report.reportName}" loaded successfully`, "info");
    },
    [userReports, getHeadersFromView]
  );

  const onConfirmSaveReport = useCallback(() => {
    if (newReportName === "" || !selectedDataSource) {
      showNotification("Please provide a report name", "warning");
      return;
    }

    setLoading(true);
    setLoadingOperation("saveReport");

    post(`${reportsBaseUrl}/${userId}`, {
      reportName: newReportName,
      report: {
        sql: query,
        dataSource: selectedDataSource.value,
        selectedHeaders: selectedHeaders,
      },
    })
      .then(() => {
        getUserReports();
        setShowReportNameDialog(false);
        setNewReportName("");
        showNotification(`Report "${newReportName}" saved successfully`, "success");
      })
      .catch(() => {
        showNotification("Failed to save report", "error");
      })
      .finally(() => {
        setLoading(false);
        setLoadingOperation(null);
      });
  }, [newReportName, query, selectedDataSource, selectedHeaders, userId, getUserReports]);

  const onDeleteReport = useCallback(() => {
    setShowDeleteDialog(false);

    if (!selectedReport) {
      return;
    }

    setLoading(true);
    setLoadingOperation("deleteReport");

    del(`${reportsBaseUrl}/${selectedReport.value}/${userId}`)
      .then(() => {
        resetAll();
        getUserReports();
        showNotification(`Report deleted successfully`, "success");
      })
      .catch(() => {
        showNotification("Failed to delete report", "error");
      })
      .finally(() => {
        setLoading(false);
        setLoadingOperation(null);
      });
  }, [selectedReport, userId, getUserReports]);

  const resetAll = useCallback(() => {
    setSelectedDataSource(null);
    setSelectedHeaders([]);
    setSelectedReport(null);
    setUpdateMode(false);
    setQuery(initialQuery);
    setReportData([]);
    setExecQueryError([]);
    setExpanded(true); // Open the query builder for new report creation
  }, []);

  const onSelectDataSource = useCallback(
    (v: selectOption) => {
      getHeadersFromView(v.value);
      setSelectedDataSource(v);
      setQuery(initialQuery);
      setSelectedHeaders([]);
      if (selectedReport) {
        setUpdateMode(true);
      }

      // Auto-scroll to field selection after data source is selected
      setTimeout(() => {
        fieldSelectRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    },
    [getHeadersFromView, selectedReport]
  );

  const onClickSaveReport = useCallback(() => {
    if (updateMode) {
      if (!selectedReport || !selectedDataSource || !selectedHeaders) {
        showNotification("Missing required information to update report", "warning");
        return;
      }

      setLoading(true);
      setLoadingOperation("updateReport");

      put(`${reportsBaseUrl}/${selectedReport.value}/${userId}`, {
        reportName: selectedReport.label,
        report: {
          sql: query,
          dataSource: selectedDataSource.value,
          selectedHeaders: selectedHeaders,
        },
      })
        .then(() => {
          getUserReports();
          setUpdateMode(false);
          showNotification(`Report "${selectedReport.label}" updated successfully`, "success");
        })
        .catch(() => {
          showNotification("Failed to update report", "error");
        })
        .finally(() => {
          setLoading(false);
          setLoadingOperation(null);
        });
    } else {
      if (!selectedDataSource || !selectedHeaders.length) {
        showNotification("Please select data source and at least one field", "warning");
        return;
      }
      setShowReportNameDialog(true);
    }
  }, [
    updateMode,
    selectedReport,
    selectedDataSource,
    selectedHeaders,
    query,
    userId,
    getUserReports,
  ]);

  const onChangeDataField = useCallback(
    (
      _e: React.SyntheticEvent,
      v: AutocompleteValue<{ label: string; value: string }, true, false, false>
    ) => {
      if (selectedReport) {
        setUpdateMode(true);
      }
      setSelectedHeaders(
        v.map((value) => {
          return {
            label: value.label,
            value: value.value,
          };
        })
      );
    },
    [selectedReport]
  );

  useEffect(() => {
    get(listPublicViews).then((res) => {
      setDataSource(
        res.data.map((viewName: { table_name: string }) => {
          return {
            label: toTitleCase(viewName.table_name, "dataSource"),
            value: viewName.table_name,
          };
        })
      );
    });
    getUserReports();
  }, [getUserReports]);

  return (
    <DashboardLayout>
      <Paper
        elevation={2}
        sx={{
          bgcolor: "#ffffff",
          padding: { xs: "16px", sm: "24px" },
          borderRadius: "16px",
          mb: 4,
          overflow: "hidden",
          transition: "all 0.3s ease",
          maxWidth: "100%",
        }}
      >
        <Grid container flexDirection={"column"} gap={4}>
          <Grid container alignItems="center" sx={{ mb: 1 }}>
            <Typography
              variant="h5"
              fontWeight="600"
              color="primary.dark"
              sx={{
                marginRight: "auto",
                position: "relative",
                "&:after": {
                  content: '""',
                  position: "absolute",
                  width: "40px",
                  height: "3px",
                  bgcolor: "primary.main",
                  bottom: "-8px",
                  left: 0,
                  borderRadius: "2px",
                },
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
              }}
            >
              Report Management
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: { xs: "wrap", md: "nowrap" },
                justifyContent: { xs: "flex-end", sm: "flex-end" },
              }}
            >
              <Tooltip
                title="Create a new report from scratch"
                placement="top"
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 400 }}
                arrow
              >
                <MDButton
                  variant="gradient"
                  color="primary"
                  onClick={() => resetAll()}
                  disabled={loading && loadingOperation === "createNew"}
                  sx={{ transition: "all 0.3s", mb: { xs: 1, md: 0 } }}
                  aria-label="Create new report"
                >
                  Create New Report
                </MDButton>
              </Tooltip>
              <Tooltip
                title={!selectedReport ? "Select a report to delete" : "Delete current report"}
                placement="top"
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 400 }}
                arrow
              >
                <span>
                  <MDButton
                    variant="gradient"
                    color="error"
                    onClick={() => selectedReport && setShowDeleteDialog(true)}
                    disabled={!selectedReport || (loading && loadingOperation === "deleteReport")}
                    sx={{ transition: "all 0.3s", mb: { xs: 1, md: 0 } }}
                    aria-label="Delete report"
                  >
                    {loading && loadingOperation === "deleteReport" ? (
                      <CircularProgress size={20} color="inherit" aria-label="Deleting report" />
                    ) : (
                      "Delete Report"
                    )}
                  </MDButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  !selectedDataSource
                    ? "Select a data source first"
                    : updateMode
                    ? "Update existing report"
                    : "Save as new report"
                }
                placement="top"
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 400 }}
                arrow
              >
                <span>
                  <MDButton
                    variant="gradient"
                    color="dark"
                    onClick={onClickSaveReport}
                    disabled={
                      !selectedDataSource ||
                      (loading &&
                        (loadingOperation === "saveReport" || loadingOperation === "updateReport"))
                    }
                    sx={{ transition: "all 0.3s", mb: { xs: 1, md: 0 } }}
                    aria-label={updateMode ? "Update report" : "Save report"}
                  >
                    {loading &&
                    (loadingOperation === "saveReport" || loadingOperation === "updateReport") ? (
                      <CircularProgress
                        size={20}
                        color="inherit"
                        aria-label={updateMode ? "Updating report" : "Saving report"}
                      />
                    ) : updateMode ? (
                      "Update Report"
                    ) : (
                      "Save Report"
                    )}
                  </MDButton>
                </span>
              </Tooltip>
            </Box>

            {/* Report Name Dialog */}
            <Dialog
              open={showReportNameDialog}
              onClose={() => setShowReportNameDialog(false)}
              aria-labelledby="report-name-dialog-title"
              aria-describedby="report-name-dialog-description"
              maxWidth="sm"
              fullWidth
              TransitionComponent={Fade}
              transitionDuration={300}
              PaperProps={{
                elevation: 5,
                sx: {
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: "1px solid rgba(0,0,0,0.05)",
                },
              }}
            >
              <DialogTitle
                id="report-name-dialog-title"
                sx={{
                  background: "linear-gradient(135deg, #e0f7fa 0%, #80deea 100%)",
                  color: "primary.dark",
                  py: 2.5,
                  fontWeight: 600,
                  fontSize: "1.25rem",
                }}
              >
                Create New Report
              </DialogTitle>
              <DialogContent sx={{ padding: "24px", overflow: "visible" }}>
                <DialogContentText
                  id="report-name-dialog-description"
                  sx={{ mb: 3, color: "text.secondary" }}
                >
                  Give your report a descriptive name that will help you identify it later.
                </DialogContentText>
                <TextField
                  id="new-report-name"
                  label="Report Name"
                  variant="outlined"
                  fullWidth
                  autoFocus
                  value={newReportName}
                  onChange={(event) => setNewReportName(event.target.value)}
                  inputProps={{ maxLength: 100, "aria-label": "Enter report name" }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      transition: "all 0.2s ease",
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                        borderWidth: "1px",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                        boxShadow: "0 0 0 3px rgba(38, 198, 218, 0.1)",
                      },
                    },
                  }}
                  helperText={newReportName ? `${newReportName.length}/100 characters` : "Required"}
                />
              </DialogContent>
              <DialogActions sx={{ padding: "0 24px 24px" }}>
                <MDButton
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => setShowReportNameDialog(false)}
                  startIcon={<Cancel />}
                  aria-label="Cancel creating report"
                >
                  Cancel
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="primary"
                  size="small"
                  onClick={onConfirmSaveReport}
                  startIcon={<Save />}
                  disabled={(loading && loadingOperation === "saveReport") || !newReportName.trim()}
                  aria-label="Save report"
                >
                  {loading && loadingOperation === "saveReport" ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Save"
                  )}
                </MDButton>
              </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
              open={showDeleteDialog}
              onClose={() => setShowDeleteDialog(false)}
              aria-labelledby="delete-dialog-title"
              aria-describedby="delete-dialog-description"
              maxWidth="xs"
              fullWidth
              TransitionComponent={Fade}
              transitionDuration={300}
            >
              <DialogTitle id="delete-dialog-title" sx={{ pt: 3, fontWeight: 600 }}>
                Delete Report
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="delete-dialog-description">
                  Are you sure you want to delete the report &quot;{selectedReport?.label}&quot;?
                  This action cannot be undone.
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3 }}>
                <MDButton
                  variant="outlined"
                  onClick={() => setShowDeleteDialog(false)}
                  color="info"
                  aria-label="Cancel deletion"
                >
                  Cancel
                </MDButton>
                <MDButton
                  variant="gradient"
                  onClick={onDeleteReport}
                  color="error"
                  startIcon={<DeleteIcon />}
                  disabled={loading && loadingOperation === "deleteReport"}
                  aria-label="Confirm deletion"
                >
                  {loading && loadingOperation === "deleteReport" ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Delete"
                  )}
                </MDButton>
              </DialogActions>
            </Dialog>
          </Grid>

          <Divider sx={{ opacity: 0.7, my: 1 }} />

          <Grid
            container
            gap={3}
            alignItems="center"
            sx={{
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
            }}
          >
            <Grid xs={12} md={3}>
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="500"
                  color="text.primary"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: "0.95rem",
                  }}
                >
                  <InfoOutlined fontSize="small" color="primary" />
                  Saved Reports
                </Typography>
              </Box>
              {userReports.length > 0 ? (
                <Autocomplete
                  id="saved-reports-select"
                  options={userReports.map((report) => ({
                    label: report.reportName,
                    value: report.id,
                  }))}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      padding: "3px 9px",
                      borderRadius: "8px",
                      transition: "all 0.2s ease",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      border: "1px solid rgba(0,0,0,0.08)",
                      "&:hover": {
                        borderColor: "primary.main",
                        boxShadow: "0 2px 5px rgba(0,0,0,0.08)",
                      },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={"Choose Report"}
                      aria-label="Select a saved report"
                    />
                  )}
                  onChange={(_, data) => data && onReportSelect(data.value, null)}
                  popupIcon={<ExpandMore />}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.value === value.value;
                  }}
                  value={selectedReport}
                  disableClearable={selectedReport !== null}
                  ListboxProps={{
                    "aria-label": "Saved reports list",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    p: 2,
                    border: "1px dashed rgba(0,0,0,0.1)",
                    borderRadius: "8px",
                    backgroundColor: "rgba(0,0,0,0.01)",
                    textAlign: "center",
                  }}
                  role="status"
                  aria-live="polite"
                >
                  <Typography variant="body2" color="text.secondary">
                    No saved reports found
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>

          <Paper
            elevation={1}
            sx={{
              border: "1px solid rgba(0,0,0,0.06)",
              bgcolor: "#f9f9f9",
              borderRadius: "12px",
              padding: "0px 20px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              transition: "all 0.3s ease",
              overflow: "hidden",
              "&:hover": {
                boxShadow: expanded ? "0 4px 10px rgba(0,0,0,0.08)" : "0 2px 5px rgba(0,0,0,0.06)",
              },
            }}
          >
            <Grid
              container
              flexDirection={"row"}
              justifyContent={"space-between"}
              alignItems="center"
              onClick={() => setExpanded(!expanded)}
              sx={{
                padding: "16px 0",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.01)" },
                borderRadius: "12px 12px 0 0",
              }}
              role="button"
              aria-expanded={expanded}
              aria-controls="query-builder-panel"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  setExpanded(!expanded);
                  e.preventDefault();
                }
              }}
            >
              <Box
                sx={{
                  fontSize: "1.05rem",
                  fontWeight: "600",
                  color: "primary.dark",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 24,
                    bgcolor: "primary.main",
                    display: "inline-block",
                    borderRadius: 1,
                    mr: 1,
                  }}
                  aria-hidden="true"
                />
                Query Builder
              </Box>
              <ExpandMore
                sx={{
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                  color: "primary.main",
                }}
                aria-hidden="true"
              />
            </Grid>
            <Collapse
              in={expanded}
              timeout={300}
              unmountOnExit
              sx={{ margin: "8px 0px 16px" }}
              id="query-builder-panel"
            >
              <Grid
                container
                gap={2}
                sx={{
                  bgcolor: "#ffffff",
                  borderRadius: "12px",
                  padding: { xs: "16px", sm: "20px" },
                  boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)",
                  border: "1px solid rgba(0,0,0,0.03)",
                }}
              >
                <Grid
                  container
                  flexDirection={"row"}
                  spacing={3}
                  sx={{ flexDirection: { xs: "column", md: "row" } }}
                >
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="500"
                      color="text.primary"
                      sx={{ mb: 1.5, fontSize: "0.95rem" }}
                      id="data-source-label"
                    >
                      Data Source
                    </Typography>
                    {loading && !selectedDataSource ? (
                      <Skeleton
                        variant="rounded"
                        height={56}
                        width="100%"
                        aria-label="Loading data sources"
                      />
                    ) : (
                      <Autocomplete
                        id="data-source-select"
                        options={dataSource}
                        sx={{
                          width: "100%",
                          "& .MuiOutlinedInput-root": {
                            padding: "3px 9px",
                            borderRadius: "8px",
                            transition: "all 0.2s ease",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                            border: "1px solid rgba(0,0,0,0.08)",
                            "&:hover": {
                              borderColor: "primary.main",
                              boxShadow: "0 2px 5px rgba(0,0,0,0.08)",
                            },
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Choose Data Source"
                            aria-labelledby="data-source-label"
                          />
                        )}
                        onChange={(_event, val) => val && onSelectDataSource(val)}
                        isOptionEqualToValue={(option, value) => {
                          if (!option || !value) return false;
                          return option.value === value.value;
                        }}
                        popupIcon={<ExpandMore />}
                        value={selectedDataSource}
                        disableClearable={selectedDataSource !== null}
                        ListboxProps={{
                          "aria-label": "Data sources list",
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="500"
                      color="text.primary"
                      sx={{ mb: 1.5, fontSize: "0.95rem" }}
                      id="data-fields-label"
                    >
                      Data Fields
                    </Typography>
                    <Autocomplete
                      id="data-fields-select"
                      multiple
                      disableCloseOnSelect
                      options={
                        allHeaders.map((val) => {
                          return { label: val.label, value: val.value };
                        }) || []
                      }
                      limitTags={isMobile ? 2 : 8}
                      isOptionEqualToValue={(o, v) => {
                        return o.label === v.label;
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder={selectedHeaders.length ? "" : "Choose Data Fields"}
                          aria-labelledby="data-fields-label"
                          InputProps={{
                            ...params.InputProps,
                            sx: {
                              padding: "3px 9px",
                              borderRadius: "8px",
                              transition: "all 0.2s ease",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                              border: "1px solid rgba(0,0,0,0.08)",
                              "&:hover": {
                                borderColor: "primary.main",
                                boxShadow: "0 2px 5px rgba(0,0,0,0.08)",
                              },
                            },
                          }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={index}
                            label={option.label}
                            size="small"
                            color="primary"
                            variant="filled"
                            {...getTagProps({ index })}
                            sx={{
                              m: "2px",
                              borderRadius: "4px",
                              fontWeight: 500,
                              fontSize: "0.75rem",
                            }}
                          />
                        ))
                      }
                      onChange={onChangeDataField}
                      value={selectedHeaders}
                      popupIcon={<ExpandMore />}
                      disableClearable={selectedHeaders.length > 0}
                      loading={loading && !loadingOperation}
                      ListboxProps={{
                        "aria-label": "Data fields list",
                      }}
                    />
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    width: "100%",
                    height: "1px",
                    bgcolor: "rgba(0,0,0,0.08)",
                    my: 2,
                    mx: "auto",
                  }}
                  role="separator"
                  aria-orientation="horizontal"
                />

                <Grid container flexDirection={"column"} gap={2} sx={{ mt: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="500"
                    color="text.primary"
                    sx={{ mb: 0.5, fontSize: "0.95rem" }}
                    id="query-conditions-label"
                  >
                    Query Conditions
                  </Typography>

                  <ThemeProvider theme={theme}>
                    <QueryBuilderMaterial>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "rgba(240, 248, 255, 0.3)",
                          borderRadius: "8px",
                          border: "1px solid rgba(38, 198, 218, 0.15)",
                        }}
                        aria-labelledby="query-conditions-label"
                      >
                        <QueryBuilder
                          fields={allHeaders}
                          query={query}
                          onQueryChange={onQueryChangeFn}
                          controlElements={customControls}
                        />
                      </Box>
                    </QueryBuilderMaterial>
                  </ThemeProvider>

                  {execQueryError.length > 0 ? (
                    <Alert
                      severity="error"
                      sx={{
                        mt: 2,
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(244, 67, 54, 0.1)",
                      }}
                      aria-live="assertive"
                    >
                      {execQueryError}
                    </Alert>
                  ) : (
                    <Box
                      sx={{
                        bgcolor: "rgba(0,0,0,0.02)",
                        p: 2.5,
                        borderRadius: "8px",
                        fontFamily: "monospace",
                        fontSize: "0.9rem",
                        overflowX: "auto",
                        border: "1px solid rgba(0,0,0,0.06)",
                        boxShadow: "inset 0 1px 3px rgba(0,0,0,0.03)",
                        color: "primary.dark",
                        mt: 2,
                        "&::-webkit-scrollbar": {
                          height: "8px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "rgba(0,0,0,0.1)",
                          borderRadius: "4px",
                        },
                      }}
                      tabIndex={0}
                      role="region"
                      aria-label="SQL query preview"
                    >
                      <pre style={{ margin: 0, overflowX: "auto" }}>
                        {formatQuery(query, "sql")}
                      </pre>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Collapse>
          </Paper>

          <Grid
            container
            gap={2}
            justifyContent="flex-start"
            sx={{
              mt: 1,
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "flex-start" },
            }}
          >
            <Tooltip
              title={
                !selectedDataSource ? "Select a data source first" : "Execute the report query"
              }
              placement="top"
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 400 }}
              arrow
            >
              <span style={{ display: "block", width: isMobile ? "100%" : "auto" }}>
                <MDButton
                  variant="gradient"
                  color="primary"
                  onClick={generateReport}
                  disabled={!selectedDataSource || (loading && loadingOperation === "runReport")}
                  sx={{
                    transition: "all 0.3s",
                    px: 3,
                    py: 1,
                    fontSize: "0.75rem",
                    width: isMobile ? "100%" : "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                  aria-label="Run report"
                >
                  {loading && loadingOperation === "runReport" ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <>
                      Run Report
                      <ArrowForward fontSize="small" />
                    </>
                  )}
                </MDButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                reportData.length === 0 ? "Run a report first" : "Export current results to CSV"
              }
              placement="top"
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 400 }}
              arrow
            >
              <span style={{ display: "block", width: isMobile ? "100%" : "auto" }}>
                <MDButton
                  variant="gradient"
                  color="dark"
                  onClick={() =>
                    exportToCSV(selectedReport?.label || "", selectedDataSource?.label || "")
                  }
                  disabled={
                    reportData.length === 0 || (loading && loadingOperation === "exportCSV")
                  }
                  sx={{
                    transition: "all 0.3s",
                    px: 3,
                    py: 1,
                    fontSize: "0.75rem",
                    width: isMobile ? "100%" : "auto",
                  }}
                  startIcon={<FileDownload />}
                  aria-label="Export to CSV"
                >
                  {loading && loadingOperation === "exportCSV" ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Export To CSV"
                  )}
                </MDButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                reportData.length === 0 ? "Run a report first" : "Export current results to PDF"
              }
              placement="top"
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 400 }}
              arrow
            >
              <span style={{ display: "block", width: isMobile ? "100%" : "auto" }}>
                <MDButton
                  variant="gradient"
                  color="dark"
                  startIcon={<PictureAsPdf />}
                  onClick={() =>
                    exportToPDF(selectedReport?.label || "", selectedDataSource?.label || "")
                  }
                  disabled={
                    reportData.length === 0 || (loading && loadingOperation === "exportPDF")
                  }
                  sx={{
                    transition: "all 0.3s",
                    px: 3,
                    py: 1,
                    fontSize: "0.75rem",
                    width: isMobile ? "100%" : "auto",
                  }}
                  aria-label="Export to PDF"
                >
                  {loading && loadingOperation === "exportPDF" ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Export To PDF"
                  )}
                </MDButton>
              </span>
            </Tooltip>
          </Grid>
          <Grid sx={{ overflowX: "scroll", maxWidth: "100%" }}>
            {reportData.length > 0 ? (
              <Paper
                elevation={1}
                sx={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  mt: 1,
                  border: "1px solid rgba(0,0,0,0.06)",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <Box sx={{ p: 2, bgcolor: "rgba(38, 198, 218, 0.08)" }} aria-live="polite">
                  <Typography variant="subtitle1" fontWeight="600" color="primary.dark">
                    Results: {reportData.length} {reportData.length === 1 ? "record" : "records"}{" "}
                    found
                  </Typography>
                </Box>
                <DataTable
                  table={{
                    columns: selectedHeaders.map((h: selectOption) => {
                      return {
                        Header: h.label,
                        accessor: h.value,
                      };
                    }),
                    rows: reportData,
                  }}
                  entriesPerPage={{ defaultValue: 100, entries: [100, 500, 1000] }}
                  onSelectItem={() => {}}
                  onRowClick={() => {}}
                  hasFilterComponent={true}
                />
              </Paper>
            ) : reportData.length === 0 && loading === false && loadingOperation !== "runReport" ? (
              <Box
                sx={{
                  p: 4,
                  textAlign: "center",
                  bgcolor: "rgba(0,0,0,0.01)",
                  borderRadius: "12px",
                  border: "1px dashed rgba(0,0,0,0.1)",
                }}
                role="status"
                aria-live="polite"
              >
                <Typography variant="body1" color="text.secondary">
                  Run a report to see results here
                </Typography>
              </Box>
            ) : null}
          </Grid>
        </Grid>
      </Paper>

      {/* Notification Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", boxShadow: "0 2px 10px rgba(0,0,0,0.15)" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default withDommer(Reports);
